import React, { useContext } from 'react';
import { ChildrenProp, ClassNameProp } from '../common-types';
import { CommonInputProps } from './CommonInputProps';
import { HBox, VBox } from '../layout';
import { once } from 'lodash';
import { ValidationErrorMessage } from './internal';

/**
 * Type used by the Context created through {@link createRadioGroupContext}.
 * @typeParam T the type of the 'value'
 * @author agd
 */
type RadioGroupContextType<T> =
  | {
      value: T | null;
      onChange: CommonInputProps<T>['onChange'];
      onBlur?: CommonInputProps<T>['onBlur'];
      onFocus?: CommonInputProps<T>['onFocus'];
      valueComparator?: (contextValue?: T | null, value?: T) => boolean;
    }
  | undefined;

/**
 * Creates a new context with the {@link RadioGroupContextType} typing.
 * @typeParam T type parameter that will be passed to the {@link RadioGroupContextType} type
 * @author agd
 */
const createRadioGroupContext = once(<T,>() =>
  React.createContext<RadioGroupContextType<T>>(undefined)
);

/**
 * Hook to use the context created by a {@link RadioGroup} component.
 * @typeParam the type of the 'value' property passed to a {@link RadioGroup}.
 * @author agd
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useRadioGroupContext = <T,>() =>
  useContext<RadioGroupContextType<T>>(createRadioGroupContext<T>());

/**
 * Props for a {@link RadioGroup} component.
 * @typeParam T the type of the value
 * @author agd
 */
export interface RadioGroupProps<T>
  extends Required<ChildrenProp>,
    ClassNameProp,
    Omit<
      CommonInputProps<T>,
      'label' | 'debounceWait' | 'defaultValue' | 'value'
    > {
  /**
   * If displayed as a row or not.
   */
  row?: boolean;
  /**
   * Optional function to use to determine if a Radio is checked or not.
   * If this option is not passed, then a simple object comparison is used.
   * @param contextValue current value in context
   * @param value value to compare the state to
   * @return true if equal according to the comparison, else false
   */
  valueComparator?: (contextValue?: T | null, value?: T) => boolean;
  /** @see {@link CommonInputProps.value } */
  value: T | null;
  /** @see {@link CommonInputProps.onChange } */
  onChange: Exclude<CommonInputProps<T>['onChange'], undefined>;
}

/**
 * Component to manage multiple Radio components as a single group using an
 * arbitrarily typed value to determine which Radio is checked.
 *
 * @typeParam T value type
 * @param properties a {@link RadioGroupProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function RadioGroup<T>({
  row = false,
  className = '',
  ...props
}: RadioGroupProps<T>): JSX.Element {
  const RadioGroupContext = createRadioGroupContext<T>();
  const wrapperClassName = `ehs-radio-group-wrapper ${className}`.trim();
  const groupClassName = `ehs-radio-group ${
    props.error ? 'invalid' : 'valid'
  }`.trim();
  // change handler
  const onChange = (val: T) => {
    props.onChange(val);
  };
  return (
    <RadioGroupContext.Provider
      value={{
        value: props.value,
        onChange: onChange,
        onBlur: props.onBlur,
        onFocus: props.onFocus,
        valueComparator: props.valueComparator
      }}
    >
      <div className={wrapperClassName}>
        {row ? (
          <HBox className={groupClassName}>{props.children}</HBox>
        ) : (
          <VBox className={groupClassName}>{props.children}</VBox>
        )}
        {props.error && <ValidationErrorMessage error={props.error} />}
      </div>
    </RadioGroupContext.Provider>
  );
}
